import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { dateFormat } from '~/utils/date-format';
import { ENGAGEMENT_HISTORY_SORT_KEYS } from '~/utils/listQueryParams';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// Redux Actions
import { fetchQrCodeScannedContacts } from '~/redux/actions/qr-codes-actions';

// MUI Components
import { Box, Typography } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import useListUrlQuery from '~/hooks/useListUrlQuery';
import useSortParams from '~/hooks/useSortParams';

// Components
import NoRecords from '~/components/General/NoRecords';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import GlobalStyles from '@mui/material/GlobalStyles';

// Icons
import { CustomFieldIcon } from '~/assets/images';
import TruncatedText from '~/components/General/TruncatedText';

// A history row links to its contact; a deleted contact has no id left on the row (nothing to open).
const getHistoryContactId = (row) => row?.contactId ?? row?.contactData?.id ?? null;

const QrCodeHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // Redux State
  const scannedContacts = useSelector((state) => state.qrCodesReducers.scannedContacts);
  const totalCount = useSelector((state) => state.qrCodesReducers.scannedContactsRowsCount);
  const loading = useSelector((state) => state.qrCodesReducers.scannedContactsLoader);
  const from = useSelector((state) => state.qrCodesReducers.scannedContactsFrom);
  const to = useSelector((state) => state.qrCodesReducers.scannedContactsTo);
  const lastPage = useSelector((state) => state.qrCodesReducers.scannedContactsLastPage);
  const currentPage = useSelector((state) => state.qrCodesReducers.scannedContactsCurrentPage);
  const perPage = useSelector((state) => state.qrCodesReducers.scannedContactsPerPage);

  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [loadedRequestKey, setLoadedRequestKey] = useState(null);
  // The column sort lives in the URL (the source of truth), so it survives reload and shared links.
  const { sort, updateUrl: updateHistoryUrl } = useListUrlQuery(ENGAGEMENT_HISTORY_SORT_KEYS);

  const qrCodeId = params.id;
  const sortKey = `${sort.sortBy ?? ''}:${sort.sortOrder ?? ''}`;
  const requestKey = `${qrCodeId}:${pagination.page}:${pagination.pageSize}:${sortKey}`;

  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!qrCodeId) return;

      await dispatch(
        fetchQrCodeScannedContacts(qrCodeId, {
          page: pagination.page,
          pageSize: pagination.pageSize,
          ...sort,
        })
      );

      if (isActive) {
        setLoadedRequestKey(requestKey);
      }
    };

    if (qrCodeId) {
      loadHistory();
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, qrCodeId, sort.sortBy, sort.sortOrder]);

  const showLoader = loadedRequestKey !== requestKey || loading;
  const pageSizes = [20, 50, 100];

  const handleChangePage = (event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleBackToQrCodes = () => {
    navigate({
      pathname: '/engagement/qr-codes',
      search: location.state?.listQuery ? `?${location.state.listQuery}` : '',
    });
  };

  // Opens the contact (Ctrl/Cmd-click: new tab, as in the Contacts table).
  const handleRowClick = (rowParams, event) => {
    const contactId = getHistoryContactId(rowParams.row);

    if (!contactId) return;

    if (event.ctrlKey || event.metaKey) {
      window.open(`/contacts/${contactId}`, '_blank');
    } else {
      navigate(`/contacts/${contactId}`);
    }
  };

  const columns = [
    {
      field: 'lastScan',
      headerName: 'Last Scan',
      flex: 1,
      minWidth: 220,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{dateFormat(params.row?.updatedAt) || '-'}</Typography>
      ),
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          text={params.row?.contact?.firstName + ' ' + params.row?.contact?.lastName || ''}
          maxLength={30}
        />
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText text={params.row?.contact?.companyName || ''} maxLength={30} />
      ),
    },
    {
      field: 'mailingAddress',
      headerName: 'Mailing Address',
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          htmlElement={
            <>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {params.row?.contact?.address1 + ' ' + params.row?.contact?.address2}
                <br />
                {params.row?.contact?.city +
                  ', ' +
                  params.row?.contact?.state +
                  ' ' +
                  params.row?.contact?.zip}
              </Typography>
            </>
          }
          text={
            `${params?.row?.address1 || ''} ${params?.row?.address2 || ''}` +
            `${params?.row?.city || ''} ` +
            `${params?.row?.state || ''} ` +
            `${params?.row?.zip || ''} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'utmCampaignName',
      sortKey: 'campaignName',
      headerName: 'UTM Campaign Name',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          text={params?.row?.campaignName || params?.row?.utmCampaignName || ''}
          maxLength={30}
        />
      ),
    },
    {
      field: 'totalScans',
      headerName: 'Total Scans',
      width: 120,
      sortable: true,
      renderCell: (params) => {
        return <Typography variant="body2">{params.row.totalScans || 0}</Typography>;
      },
    },
  ];

  // A new sort goes to the URL and the table returns to page 1; the fetch effect above reloads it.
  const { sortProps } = useSortParams(
    columns,
    (nextSort) => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      updateHistoryUrl({ sort: nextSort });
    },
    sort
  );

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodeHistoryPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodeHistoryScreenCard">
          <Box className="qrHistoryHeader olcHeaderWrap">
            <OlcPageBackButton onClick={handleBackToQrCodes}>Back to QR Codes</OlcPageBackButton>
          </Box>

          {!showLoader && scannedContacts.length === 0 ? (
            <Box>
              <NoRecords
                icon={
                  <img
                    src={CustomFieldIcon}
                    style={{ height: '33px', width: '33px' }}
                    alt="no-records"
                  />
                }
                title="No QR Code History"
                message={
                  <>
                    <p style={{ margin: '0px' }}>No QR code scanning history available yet.</p>
                    <p style={{ margin: '0px' }}>
                      QR codes will appear here once they are scanned.
                    </p>
                  </>
                }
              />
            </Box>
          ) : (
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${scannedContacts.length === 0 ? 'table-empty' : ''}`}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  rows={showLoader ? [] : scannedContacts}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  {...sortProps}
                  rowHeight={61}
                  loading={showLoader}
                  onRowClick={handleRowClick}
                  getRowClassName={(rowParams) =>
                    getHistoryContactId(rowParams.row) ? 'clickableRow' : ''
                  }
                  sx={{
                    '& .MuiDataGrid-row.clickableRow:hover': {
                      cursor: 'pointer',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                />
              </Box>

              {(showLoader || totalCount > 0) && (
                <OlcTablePagination
                  count={lastPage || 1}
                  page={currentPage || 1}
                  pageSize={perPage || pagination.pageSize}
                  pageSizes={pageSizes}
                  onPageChange={handleChangePage}
                  onPageSizeChange={(event) => {
                    setPagination({ page: 1, pageSize: event.target.value });
                  }}
                  from={from}
                  to={to}
                  total={totalCount}
                  loading={showLoader}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default QrCodeHistory;
